import React, { ReactNode } from 'react';

import { usePartnerLinkingImage } from 'microsites/partner-linking/use-partner-linking-images';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import TimHortonsLogo from 'components/icons/tim-hortons-logo';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${Styles.color.background};
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Styles.color.primary};
  // Match the main app nav bar (height + red) so the microsite header is
  // consistent with the rest of the site.
  height: ${Styles.layout.navHeight.mobile};
  padding: 0 1rem;

  ${Styles.desktop`
    height: ${Styles.layout.navHeight.desktop};
  `}

  svg {
    height: 1.5rem;
    width: auto;

    // TH brand logo is red by default; invert to white over the red banner.
    path {
      fill: ${Styles.color.white};
    }
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  // Both breakpoints follow the design: content is top-aligned below the
  // header (never vertically centered). Mobile sits 2rem below the header;
  // desktop uses the larger ~6.5rem gap above the illustration (Figma
  // Partnerships node 1377:61836).
  justify-content: flex-start;
  text-align: center;
  padding: 2rem 1rem;
  margin: 0 auto;
  width: 100%;
  max-width: 24rem;

  ${Styles.desktop`
    max-width: ${Styles.layout.smallerSectionMaxWidth};
    padding: 6.5rem 1rem 3rem;
  `}
`;

// Illustration comes from the featurePartnerLinking singleton in Sanity.
const IllustrationContainer = styled.div`
  margin-bottom: 1rem;

  img {
    width: 12.5rem;
    height: 12.5rem;
  }
`;

const Title = styled.h1`
  color: ${Styles.color.black};
  font-family: ${Styles.fontFamily.base};
  font-size: 1.5rem;
  line-height: 2rem;
  margin: 0.5rem 0;

  ${Styles.desktop`
    font-size: 2rem;
    line-height: 2.5rem;
  `}
`;

const Description = styled.p`
  // Figma Tokens/secondary-1 — the design system token isn't wired into Styles.
  color: #654d4d;
  font-size: 1rem;
  line-height: 1.5rem;
  margin: 0 0 2rem 0;
`;

const SupportLink = styled.a`
  color: inherit;
  text-decoration: underline;
`;

const CtaWrapper = styled.div`
  // Hidden on mobile: users reach this screen from the app and can return there
  // natively, so the design omits the CTA. Shown on desktop web only.
  display: none;
  width: 100%;
  max-width: 20rem;

  ${Styles.desktop`
    display: block;
  `}
`;

interface IRateLimitedProps {
  onBackToApp: () => void;
}

const RateLimited: React.FC<IRateLimitedProps> = ({ onBackToApp }) => {
  const { formatMessage } = useIntl();
  const { imageUrl: rateLimitedImageUrl } = usePartnerLinkingImage('rateLimitedIllustration');

  // Reuse the shared, locale-aware help-centre link (en-ca / fr-ca) instead of
  // hardcoding the URLs here.
  const supportUrl = formatMessage({ id: 'helpCenterLink' });
  const renderSupportLink = (chunks: ReactNode) => (
    <SupportLink href={supportUrl} target="_blank" rel="noopener noreferrer">
      {chunks}
    </SupportLink>
  );

  return (
    <Page data-testid="ct-rate-limited">
      <Helmet title={formatMessage({ id: 'ctLinkingRateLimitedTitle' })} />
      <Header>
        <TimHortonsLogo />
      </Header>
      <Content>
        <IllustrationContainer aria-hidden>
          {rateLimitedImageUrl && <img src={rateLimitedImageUrl} alt="" />}
        </IllustrationContainer>
        <Title>{formatMessage({ id: 'ctLinkingRateLimitedTitle' })}</Title>
        <Description>
          {formatMessage(
            { id: 'ctLinkingRateLimitedDescription' },
            { contactSupport: renderSupportLink }
          )}
        </Description>
        <CtaWrapper>
          <ActionButton fullWidth onClick={onBackToApp}>
            {formatMessage({ id: 'ctLinkingRateLimitedBackToAppCta' })}
          </ActionButton>
        </CtaWrapper>
      </Content>
    </Page>
  );
};

export default RateLimited;
